/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { BookText, CircleCheck, FileClock, Landmark, RefreshCw, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '@/api';
import type { CompliancePolicySummary, PolicyKind, PolicyLifecycle } from '@/api/areas/governance';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatCount, formatDate } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { EnumBadge, SelectField, useEnumOptions } from '../compliance/ui';
import { policyLabel } from '@/lib/labels';

const POLICY_STATUS = {
  DRAFT: { tone: 'warn' },
  APPROVED: { tone: 'info' },
  ACTIVE: { tone: 'ok' },
  RETIRED: { tone: 'neutral' },
  REJECTED: { tone: 'neutral' },
} as const;

const ORIGIN = { DATABASE: { tone: 'info' }, FILE: { tone: 'neutral' }, BOTH: { tone: 'ok' } } as const;

/**
 * `/compliance/policies`: the versioned rules the platform applies, one row per policy. With
 * `tld` (the TLD page): only the policies that apply to that TLD, with why.
 */
export function PoliciesPage({ tld }: { tld?: { id: string; zone: string } } = {}) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const options = useEnumOptions();
  useDocumentTitle(t('policies.title'));
  const [params, setParams] = useSearchParams();
  const kind = (params.get('kind') ?? '') as PolicyKind | '';
  const status = (params.get('status') ?? '') as PolicyLifecycle | '';
  const q = params.get('q') ?? '';
  const result = useApi(
    (signal) => api.listCompliancePolicies({ kind: kind || undefined, status: status || undefined, q: q || undefined, limit: 200, tld_id: tld?.id }, { signal }),
    [kind, status, q, tld?.id],
  );
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  const filtered = Boolean(kind || status || q);
  const items = result.status === 'success' ? result.data.items : [];
  const authorities = new Set(items.map((p) => p.authority));
  const drafts = items.filter((p) => p.status === 'DRAFT').length;

  const columns: Column<CompliancePolicySummary>[] = [
    {
      key: 'policy',
      header: t('policies.col.policy'),
      // The legal source is long: it belongs on the policy's own page, not in every row.
      cell: (p) => <EntityRef label={policyLabel(p.policy_key)} sub={<code className="font-mono">{p.policy_key}</code>} to={`/compliance/policies/${encodeURIComponent(p.policy_key)}`} />,
    },
    ...(tld
      ? [
          {
            key: 'why',
            header: t('policies.col.appliesBecause'),
            cell: (p: CompliancePolicySummary) => <span className="text-sm">{p.applies_because ? t(`policies.because.${p.applies_because}`, { zone: tld.zone }) : '—'}</span>,
          },
        ]
      : []),
    {
      key: 'kind',
      header: t('policies.col.kind'),
      cell: (p) => <EnumBadge group="policyKind" value={p.kind} tone="neutral" icon={false} />,
    },
    {
      key: 'authority',
      header: t('policies.col.authority'),
      cell: (p) => (
        <div className="text-sm">
          {p.authority}
          <div className="text-xs text-muted-foreground">{p.owner}</div>
        </div>
      ),
    },
    {
      key: 'version',
      header: t('policies.col.version'),
      cell: (p) => (
        <div className="text-sm">
          <span className="font-medium">{p.version}</span>
          {p.file_version && p.file_version !== p.version ? <div className="text-xs text-muted-foreground">{p.file_version}</div> : null}
          {p.version_count > 1 ? (
            <div className="text-xs text-muted-foreground">
              {t('policies.col.versions')}: {p.version_count}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('policies.col.status'),
      // In force implies ACTIVE, so one badge says everything an operator needs.
      cell: (p) => (p.in_force ? <Badge tone="ok">{t('policies.inForce')}</Badge> : <EnumBadge group="policyStatus" value={p.status} tones={POLICY_STATUS} />),
    },
    {
      key: 'effective',
      header: t('policies.col.effective'),
      cell: (p) => (
        <span className="text-sm whitespace-nowrap">
          {p.effective_until
            ? t('policies.fromUntil', { from: formatDate(p.effective_from), until: formatDate(p.effective_until) })
            : t('policies.since', { from: formatDate(p.effective_from) })}
        </span>
      ),
    },
    {
      key: 'origin',
      header: t('policies.col.origin'),
      cell: (p) => <EnumBadge group="policyOrigin" value={p.origin} tones={ORIGIN} icon={false} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={tld ? t('policies.forTld', { zone: `.${tld.zone}` }) : t('policies.title')}
        crumbs={tld ? undefined : [{ label: t('crumbs.compliance') }]}
        subtitle={tld ? t('policies.forTldSubtitle') : t('policies.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        {drafts > 0 ? <Callout tone="warn">{t('policies.draftNote')}</Callout> : null}
        {result.status === 'success' && !tld ? (
          <StatGrid>
            <StatCard label={t('policies.stats.total')} value={formatCount(items.length)} icon={BookText} />
            <StatCard label={t('policies.stats.inForce')} value={formatCount(result.data.in_force_count)} tone="ok" icon={CircleCheck} />
            <StatCard label={t('policies.stats.drafts')} value={formatCount(drafts)} tone={drafts > 0 ? 'warn' : 'neutral'} icon={FileClock} />
            <StatCard label={t('policies.stats.authorities')} value={formatCount(authorities.size)} icon={Landmark} />
          </StatGrid>
        ) : null}
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              filtered ? (
                <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('policies.filter.kind')}
              value={kind}
              allLabel={t('common.any')}
              options={options('policyKind', result.status === 'success' ? result.data.kinds : [])}
              onChange={(v) => set('kind', v)}
              className="w-full sm:w-56"
            />
            <SelectField
              label={t('policies.filter.status')}
              value={status}
              allLabel={t('common.any')}
              options={options('policyStatus', result.status === 'success' ? result.data.statuses : [])}
              onChange={(v) => set('status', v)}
              className="w-full sm:w-48"
            />
            <Field label={t('policies.filter.search')} className="w-full sm:w-64">
              <Input
                key={q}
                type="search"
                defaultValue={q}
                onKeyDown={(e) => e.key === 'Enter' && set('q', e.currentTarget.value.trim())}
                onBlur={(e) => set('q', e.target.value.trim())}
                placeholder={t('policies.searchHint')}
              />
            </Field>
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={5} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('policies.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? (
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(p) => p.policy_key}
              empty={<EmptyState title={tld ? t('policies.emptyTld') : t('policies.empty')}>{filtered ? t('policies.emptyHint') : null}</EmptyState>}
            />
          ) : null}
        </Section>
      </div>
    </>
  );
}
